import React, { useEffect, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { Team } from './Team';
import { BUTTON_GHOST, BUTTON_QUIET, SECTION_HINT, SECTION_TITLE } from '../../components/portal/adminStyles';
import { fetchUnhealthyWorkers, lastRunLabel, workerConsequence, workerName, type WorkerHeartbeat } from '../../utils/workerHealth';

/**
 * Settings — the account, the people, and the machinery.
 *
 * **Built 5 Nov 2026, because the front page was already promising it.** The
 * Settings tile carried the line "Team · access · workers" and opened the
 * Team page: two thirds of that sentence was aspiration. Tom, on the account
 * menu in the corner of the header: *"shouldn't the 'tom' account name go
 * into settings?"* — which is right, and which only works if Settings is a
 * real screen.
 *
 * Three sections, in the order you would need them:
 *
 *   1. **Account.** Who you are signed in as, and the way out. It is first
 *      because it is the one thing here that is about the person reading it.
 *   2. **Workers.** The scheduled jobs nobody watches. Needs Attention raises
 *      a worker only when it has already broken; this is the only place in
 *      Portal OS that answers "is everything running?" while the answer is
 *      still yes. On a good day it is one line, and that is the point.
 *   3. **Team.** The existing page, rendered inside this one with its own
 *      heading suppressed — see the `embedded` prop in pages/portal/Team.tsx.
 *      It is not reimplemented here: the invitation flow, the role handling
 *      and the resend link are all server-checked and were not worth a second
 *      copy.
 *
 * **What this screen must never become** is the place that settings screens
 * usually become — a drawer for every switch nobody could place elsewhere.
 * Portal OS has almost no preferences by design, and the right home for a
 * setting is the screen it affects. If something lands here, ask first
 * whether it belongs on the page it changes.
 */

/** Re-checked while the screen is left open, quietly. */
const REFRESH_MS = 5 * 60 * 1000;
export function Settings({
  onOpenAccount,
  onSignOut
}: {
  onOpenAccount: () => void;
  onSignOut: () => void;
}) {
  const {
    staffUser
  } = usePortal();
  const [unhealthy, setUnhealthy] = useState<WorkerHeartbeat[]>([]);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const workers = await fetchUnhealthyWorkers();
      if (cancelled) return;
      setUnhealthy(workers);
      setChecked(true);
    }
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);
  return <section aria-labelledby="settings" data-id="element-2467" data-mp="20g65v56jyk1g">
      <h1 id="settings" className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2468" data-mp="20f4ji4k36b4f">
        Settings
      </h1>

      {/* 1. Account */}
      <div className="mt-16" data-id="element-2469" data-mp="az0whpkrg35m">
        <h2 className={SECTION_TITLE} data-id="element-2470" data-mp="aztsbvi6l88z">Account</h2>
        <p className={SECTION_HINT} data-id="element-2471" data-mp="b14w3vbmmg03">Signed in on this device.</p>

        <div className="mt-8 border-t border-ruleDark pt-7" data-id="element-2472" data-mp="fwslobu0tb64">
          <p className="m-0 font-display text-2xl leading-none" data-id="element-2473" data-mp="jzyqrg5ecwb0">{staffUser?.name || 'Signed in'}</p>
          {/* `value-sm`, not `label-sm`: an address is data, and letter-spaced
              capitals are the worst possible setting for a string somebody
              reads character by character. Same rule as the Team list. */}
          {staffUser?.email && <p className="value-sm mt-3 text-paper/30" data-id="element-2474" data-mp="3sg20rsjzex2i">{staffUser.email}</p>}
          {staffUser?.role && <p className="label mt-4 text-paper/50" data-id="element-2475" data-mp="17o5f06sdh0n">{staffUser.role}</p>}

          <div className="mt-9 flex flex-wrap items-center gap-6" data-id="element-2476" data-mp="607jrzdasg6i">
            <button type="button" onClick={onOpenAccount} className={BUTTON_GHOST} data-id="element-2477" data-mp="34pxufdfesj7t">
              Change Password
            </button>
            <button type="button" onClick={onSignOut} className={BUTTON_QUIET} data-id="element-2478" data-mp="39nzdhycu4ija">
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* 2. Workers */}
      <div className="mt-20" data-id="element-2479" data-mp="1b809uz16pnd2">
        <h2 className={SECTION_TITLE} data-id="element-2480" data-mp="1b8s86hm61z71">Workers</h2>
        <p className={SECTION_HINT} data-id="element-2481" data-mp="1oydjd5tq5a4i">
          The scheduled jobs that run without anybody present — the calendar both ways, the news
          round-up, the client link check.
        </p>

        <div className="mt-8 border-t border-ruleDark" data-id="element-2482" data-mp="1ywt8525a47au">
          {!checked && <p className="label py-8 text-paper/35" data-id="element-2483" data-mp="1yuypxvle6tag">Checking…</p>}

          {/* A worker with no row at all is invisible to the health check — see
              fetchUnhealthyWorkers. So this says what was actually tested:
              nothing is late or failing. It does not claim every worker that
              ought to exist does. */}
          {checked && unhealthy.length === 0 && <p className="py-8 font-display text-xl text-paper/70" data-id="element-2484" data-mp="djqhzcv44fzo">
              Nothing is late or reporting a problem.
            </p>}

          {checked && unhealthy.map(beat => <div key={beat.worker} className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2 border-b border-ruleDark py-7" data-id="element-2485" data-mp="3vlfa6j0jvltt">
                <div className="min-w-0" data-id="element-2486" data-mp="3lt9aso7yy24l">
                  <p className="m-0 font-display text-xl leading-snug" data-id="element-2487" data-mp="3qs2niefflp3p">
                    {workerName(beat.worker)}
                    {beat.status === 'error' ? ' reported a problem' : ' has stopped running'}
                  </p>
                  <p className="label-sm mt-2 text-paper/40" data-id="element-2488" data-mp="3gv7pdwn2lqpy">{lastRunLabel(beat)}</p>
                  <p className="label-sm mt-1 text-paper/30" data-id="element-2489" data-mp="32vovo8v0ahx6">{workerConsequence(beat.worker)}</p>
                </div>
              </div>)}
        </div>
      </div>

      {/* 3. Team */}
      <div className="mt-20" data-id="element-2490" data-mp="1130kndc4eq6u">
        <h2 className={SECTION_TITLE} data-id="element-2491" data-mp="1141zlsw9bwtu">Team</h2>
        <p className={SECTION_HINT} data-id="element-2492" data-mp="1623lsmozcc9j">
          Who can sign in to Portal OS, and at what level. Owners only.
        </p>

        <div className="mt-8" data-id="element-2493" data-mp="13orlhhaiubof">
          <Team embedded data-id="element-2494" data-mp="13p1frh2ln4zx" />
        </div>
      </div>
    </section>;
}